import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, MapPin, Wifi, Car, Coffee, Dumbbell, Users, CalendarIcon, CheckCircle } from "lucide-react";
import translations from "../contexts/translations";
import hotelService from "../services/hotelService";
import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import { useAuth } from "../contexts/AuthContext";
import cmiService from "../services/cmiService"; // ✅ CMI au lieu de Stripe
import { getPaymentMode } from "../services/settingsService";
import ReviewSection from '../components/ReviewSection';
import { hotelDetailPlaceholder } from "../utils/imagePlaceholders";
import Seo, { SITE_URL, cleanDescription } from "../components/Seo";
import {
  ModernInput, 
  ModernDatePicker,
  FormSection
} from "../components/ui/FormComponents";
import { motion } from "framer-motion";
import { Mail as MailIcon } from "lucide-react";

export const serviceIcons = {
  Wifi: Wifi,
  Parking: Car,
  Restaurant: Coffee,
  Spa: Dumbbell,
  Piscine: Users,
  "Plage privée": MapPin,
  Golf: MapPin,
  Ski: MapPin,
  Sauna: Dumbbell,
};

const HotelDetails = ({ language }) => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateArrivee, setDateArrivee] = useState("");
  const [dateDepart, setDateDepart] = useState("");
  const [nombreAdultes, setNombreAdultes] = useState(1);
  const [nombreEnfants, setNombreEnfants] = useState(0);
  const [prixDetails, setPrixDetails] = useState(null);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [acceptCgv, setAcceptCgv] = useState(false);
  const t = translations[language];

  // Mode de paiement (admin) : "direct" (CMI) ou "email" (demande + lien admin)
  const [paymentMode, setPaymentMode] = useState("direct");
  const [clientNom, setClientNom] = useState("");
  const [clientTel, setClientTel] = useState("");
  const [clientAdresse, setClientAdresse] = useState("");
  useEffect(() => {
    getPaymentMode("hotel").then(setPaymentMode).catch(() => {});
  }, []);



  useEffect(() => {
    
    const fetchHotel = async () => {
      try {
        setLoading(true);
        const response = await hotelService.getHotelById(id);
        setHotel(response.data);
      } catch (error) {
        console.error("Error fetching hotel:", error);
        toast.error(t.hotelFetchError || "Failed to load hotel details");
      } finally {
        setLoading(false);
      }
    };

    fetchHotel();
    setAcceptCgv(false);
  }, [id, t.hotelFetchError]);

  const calculateNights = (dateArrivee, dateDepart) => {
    if (!dateArrivee || !dateDepart) return 1;
    
    const arrival = new Date(dateArrivee);
    const departure = new Date(dateDepart);
    const diffTime = Math.abs(departure - arrival);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };

  const calculatePrice = async () => {
    if (!hotel) return;
    
    try {
      setReservationLoading(true);
      const nbNuits = calculateNights(dateArrivee, dateDepart);
      
      const response = await hotelService.calculatePriceWithGuests(
        hotel._id,
        nombreAdultes,
        nombreEnfants,
        nbNuits,
        currentUser ? true : false,
        !currentUser ? userEmail : undefined
      );
      
      if (response.success && response.data) {
        setPrixDetails(response.data);
      } else {
        throw new Error(response.message || "Error calculating price");
      }
    } catch (error) {
      console.error("Price calculation error:", error);
      toast.error(t.priceCalculationError || "Error calculating price");
    } finally {
      setReservationLoading(false);
    }
  };

  // ✅ Nouveau handleBooking : CMI au lieu de Stripe
    const handleBooking = async () => {
    if (!dateArrivee || !dateDepart) {
      toast.error(t.datesRequired || "Please select arrival and departure dates");
      return;
    }

    const arrival = new Date(dateArrivee);
    const departure = new Date(dateDepart);
    if (arrival >= departure) {
      toast.error(t.invalidDates || "Arrival date must be before departure date");
      return;
    }

    if (!currentUser && !userEmail) {
      toast.error(t.emailRequired || "Please provide your email");
      return;
    }

    if (!prixDetails) {
      toast.error(t.priceCalculationError || "Please calculate the price first");
      return;
    }

    if (!acceptCgv) {
      toast.error(t.mustAcceptCgv || "Veuillez accepter les CGV avant de continuer vers le paiement.");
      return;
    }


    // ====== MODE "EMAIL" : on enregistre une demande, l'admin enverra le lien ======
    if (paymentMode === "email") {
      const nomComplet =
        (currentUser ? `${currentUser.prenom || ""} ${currentUser.nom || ""}`.trim() : "") || clientNom.trim();
      const email = currentUser?.email || userEmail;
      if (!nomComplet || !clientTel.trim() || !clientAdresse.trim()) {
        toast.error(t.clientInfoRequired || "Veuillez renseigner votre nom, téléphone et adresse.");
        return;
      }
      try {
        setReservationLoading(true);
        toast.loading(t.processingReservation || "Envoi de votre demande...", { id: "cmi-loading" });
        const API_BASE =
          process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL || "https://nouzhaback-theta.vercel.app";
        const res = await fetch(`${API_BASE}/api/reservations/hotels/request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            hotelId: hotel._id,
            ville: hotel.ville,
            dateArrivee,
            dateDepart,
            nombreAdultes,
            nombreEnfants,
            client: { nomComplet, email, telephone: clientTel.trim(), adresse: clientAdresse.trim() },
            lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || "Erreur lors de l'envoi de la demande");
        toast.success(
          t.demandeSent || "Demande envoyée ✅ Vous recevrez un lien de paiement par email après validation.",
          { id: "cmi-loading", duration: 6000 }
        );
      } catch (error) {
        notifyApiError(error, t.bookingError || "Échec de la demande", { id: "cmi-loading" });
      } finally {
        setReservationLoading(false);
      }
      return;
    }

    // ====== MODE "DIRECT" : paiement CMI immédiat ======
    try {
      setReservationLoading(true);
      toast.loading(
        t.processingReservation || "Processing your reservation...",
        { id: "cmi-loading" }
      );

      const payload = {
        hotelId: hotel._id,
        dateArrivee,
        dateDepart,
        nombreAdultes,
        nombreEnfants,
        utilisateur: currentUser
          ? `${currentUser.prenom || ""} ${currentUser.nom || ""}`.trim() ||
            currentUser.email
          : "Guest",
        email: currentUser?.email || userEmail,
        commentaires: "",
        lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
        prixTotal: prixDetails.prixTotal, // <<< on envoie le montant affiché
        // debug: 1,
      };

      // Appel backend CMI → HTML <form> auto-submit
      const html = await cmiService.createHotelPayment(payload);

      toast.success(
        t.redirectingToPayment || "Redirecting to payment...",
        { id: "cmi-loading", duration: 1500 }
      );

      setTimeout(() => {
        document.open();
        document.write(html);
        document.close();
      }, 500);
    } catch (error) {
      console.error("Booking error (CMI):", error);
      notifyApiError(error, t.bookingError || "Booking failed", { id: "cmi-loading" });
    } finally {
      setReservationLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-orange-700 mb-4">
          {t.hotelNotFound || "Hotel not found"}
        </h2>
        <Link to="/sejour" className="text-orange-600 hover:underline">
          {t.backToHotels || "Back to hotels list"}
        </Link>
      </div>
    );
  }

  const hotelPath = `/sejour/${id}`;
  const hotelImages = [hotel.image, ...(hotel.images || [])].filter(Boolean);
  const hotelTitle = hotel.ville ? `${hotel.nom} — ${hotel.ville}` : hotel.nom;
  const hotelJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Hotel",
      name: hotel.nom,
      description: cleanDescription(hotel.description, 280),
      ...(hotelImages.length ? { image: hotelImages } : {}),
      priceRange: "$$$",
      address: {
        "@type": "PostalAddress",
        ...(hotel.ville ? { addressLocality: hotel.ville } : {}),
        addressCountry: "MA",
      },
      ...(hotel.nb ? { starRating: { "@type": "Rating", ratingValue: String(hotel.nb) } } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Séjours", item: `${SITE_URL}/sejour` },
        { "@type": "ListItem", position: 3, name: hotel.nom, item: `${SITE_URL}${hotelPath}` },
      ],
    },
  ];

  return (
    <div className="w-full pt-16 min-h-screen bg-white relative" style={{
      backgroundImage: 'url(/src/assets/zelij3.png)',
      backgroundRepeat: 'repeat',
      backgroundSize: '400px',
      backgroundAttachment: 'fixed',
      zIndex: 0
    }}>
      <Seo
        path={hotelPath}
        title={hotelTitle}
        description={cleanDescription(
          hotel.description ||
            `Séjour de luxe à ${hotel.nom}${hotel.ville ? ` à ${hotel.ville}` : ""}, au Maroc avec Nouzha. Hébergement haut de gamme et service sur mesure.`
        )}
        keywords={`séjour ${hotel.ville || "Maroc"}, ${hotel.nom}, hôtel luxe Maroc, riad ${hotel.ville || "Maroc"}, réservation séjour Maroc`}
        image={hotel.image || undefined}
        type="article"
        jsonLd={hotelJsonLd}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/sejour" className="text-orange-600 hover:underline flex items-center">
            &larr; {t.backToHotels || "Back to hotels"}
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Hotel Header */}
          <div className="relative">
            <img
              src={hotel.image || hotelDetailPlaceholder}
              alt={hotel.nom}
              className="w-full h-64 md:h-96 object-cover"
            />
            {hotel.offre_reduction > 0 && (
              <span className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                -{hotel.offre_reduction}% {t.discount || "OFF"}
              </span>
            )}
          </div>

          {/* Hotel Info */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-3xl font-bold text-orange-700">{hotel.nom}</h1>
                  <div className="flex items-center">
                    {Array.from({ length: hotel.nb_etoiles }).map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>

                <div className="flex items-center text-gray-700 mb-4">
                  <MapPin className="h-5 w-5 text-orange-500 mr-2" />
                  <span>{hotel.ville}</span>
                </div>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {t.aboutHotel || "About this hotel"}
                  </h3>
                  <p className="text-gray-700">{hotel.description}</p>
                </div>

                {/* Services */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {t.services || "Services & Amenities"}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {hotel.services && hotel.services.map((service) => {
                      const IconComponent = serviceIcons[service];
                      return (
                        <div key={service} className="flex items-center bg-orange-50 text-orange-700 px-3 py-2 rounded-lg">
                          {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                          <span>{service}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Booking Form */}
              <div className="md:w-[450px] space-y-6">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-orange-100/50 sticky top-24">
                  <FormSection title={t.bookNow || "Réserver"} icon={CalendarIcon}>
                    <div className="space-y-6">
                      {/* Dates */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <ModernDatePicker
                          label={t.checkin || "Arrivée"}
                          value={dateArrivee}
                          onChange={(val) => setDateArrivee(val)}
                          placeholder="Select"
                        />
                        <ModernDatePicker
                          label={t.checkout || "Départ"}
                          value={dateDepart}
                          onChange={(val) => setDateDepart(val)}
                          placeholder="Select"
                          minDate={dateArrivee}
                        />
                      </div>

                      {/* Guests */}
                      <div className="p-5 bg-orange-50/50 rounded-2xl border border-orange-100/50 space-y-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="block font-bold text-gray-900">{t.adults || "Adultes"}</span>
                            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{t.adultAgeInfo || "18+ YEARS"}</span>
                          </div>
                          <div className="flex items-center gap-4 bg-white/80 p-1 rounded-xl border border-orange-200/50 shadow-sm">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setNombreAdultes(Math.max(1, nombreAdultes - 1))}
                              disabled={nombreAdultes <= 1}
                              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-100 text-gray-700 font-bold hover:bg-orange-50 disabled:opacity-30"
                            >
                              -
                            </motion.button>
                            <span className="w-8 text-center font-black text-orange-600">{nombreAdultes}</span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setNombreAdultes(nombreAdultes + 1)}
                              className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700"
                            >
                              +
                            </motion.button>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-orange-100/50">
                          <div>
                            <span className="block font-bold text-gray-900">{t.children || "Enfants"}</span>
                            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{t.childrenAgeInfo || "0-17 YEARS"}</span>
                          </div>
                          <div className="flex items-center gap-4 bg-white/80 p-1 rounded-xl border border-orange-200/50 shadow-sm">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setNombreEnfants(Math.max(0, nombreEnfants - 1))}
                              disabled={nombreEnfants <= 0}
                              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-100 text-gray-700 font-bold hover:bg-orange-50 disabled:opacity-30"
                            >
                              -
                            </motion.button>
                            <span className="w-8 text-center font-black text-orange-600">{nombreEnfants}</span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setNombreEnfants(nombreEnfants + 1)}
                              className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700"
                            >
                              +
                            </motion.button>
                          </div>
                        </div>
                      </div>

                      {!currentUser && (
                        <ModernInput
                          label="Email"
                          icon={MailIcon}
                          type="email"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          placeholder="votre@email.com"
                          required
                        />
                      )}

                      {/* Champs requis pour une demande (mode paiement par email) */}
                      {paymentMode === "email" && (
                        <>
                          <ModernInput
                            label={t.fullName || "Nom complet"}
                            icon={MailIcon}
                            type="text"
                            value={clientNom}
                            onChange={(e) => setClientNom(e.target.value)}
                            placeholder="Nom et prénom"
                          />
                          <ModernInput
                            label={t.phone || "Téléphone"}
                            icon={MailIcon}
                            type="tel"
                            value={clientTel}
                            onChange={(e) => setClientTel(e.target.value)}
                            placeholder="+212 6xx xx xx xx"
                          />
                          <ModernInput
                            label={t.address || "Adresse"}
                            icon={MailIcon}
                            type="text"
                            value={clientAdresse}
                            onChange={(e) => setClientAdresse(e.target.value)}
                            placeholder="Ville, quartier..."
                          />
                        </>
                      )}

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={calculatePrice}
                        disabled={!dateArrivee || !dateDepart || reservationLoading}
                        className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-orange-600 transition-all shadow-2xl disabled:opacity-50"
                      >
                        {reservationLoading ? "Calcul..." : (t.calculatePrice || "Calculer le prix")}
                      </motion.button>

                      {/* CGV */}
                      <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200/50 space-y-4">
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div className="relative flex items-center mt-1">
                            <input
                              type="checkbox"
                              checked={acceptCgv}
                              onChange={(e) => setAcceptCgv(e.target.checked)}
                              className="peer h-5 w-5 opacity-0 absolute cursor-pointer"
                            />
                            <div className="h-5 w-5 border-2 border-gray-300 rounded-lg group-hover:border-orange-500 peer-checked:bg-orange-600 peer-checked:border-orange-600 transition-all flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" />
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 leading-relaxed font-medium">
                            {t.acceptTermsPrefix || "J’ai lu et j’accepte les"}{" "}
                            <a href="/cgv" target="_blank" className="text-orange-600 font-bold hover:underline">
                              {t.cgvLinkLabel || "Conditions Générales de Vente"}
                            </a>{" "}
                            ainsi que les conditions de paiement via CMI.
                          </span>
                        </label>

                        <div className="flex flex-wrap items-center gap-2 pl-8">
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paiement sécurisé :</span>
                          <a href="/paiement-securise" target="_blank" className="text-xs text-orange-600 font-bold hover:underline">
                            Voir les informations CMI / Visa / MasterCard / 3DS
                          </a>
                        </div>
                      </div>

                      {/* Bouton dédié au mode "email" (pas besoin de calculer le prix) */}
                      {paymentMode === "email" && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleBooking}
                          disabled={reservationLoading || (!currentUser && !userEmail) || !acceptCgv}
                          className="w-full bg-emerald-600 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-emerald-700 transition shadow-2xl shadow-emerald-200 disabled:opacity-50"
                        >
                          {reservationLoading
                            ? (t.processing || "Envoi...")
                            : !acceptCgv
                            ? "Accepter CGV"
                            : (t.sendRequest || "Envoyer la demande")}
                        </motion.button>
                      )}
                    </div>
                  </FormSection>

                  {/* Price Details */}
                  {prixDetails && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-8 pt-8 border-t border-orange-100"
                    >
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 font-medium">{t.basePrice || "Prix de base"}</span>
                          <span className="font-bold text-gray-900">{prixDetails.prixBase}€</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 font-medium">{nombreAdultes} {t.adults || "Adultes"}</span>
                          <span className="font-bold text-gray-900">{prixDetails.prixAdultes}€</span>
                        </div>
                        {nombreEnfants > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500 font-medium">{nombreEnfants} {t.children || "Enfants"}</span>
                            <span className="font-bold text-gray-900">{prixDetails.prixEnfants}€</span>
                          </div>
                        )}
                        {prixDetails.reduction > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-green-600 font-bold uppercase tracking-tighter">{t.discount || "Réduction"} ({prixDetails.reduction}%)</span>
                            <span className="font-bold text-green-600">-{prixDetails.reductionMontant}€</span>
                          </div>
                        )}
                        {currentUser && prixDetails.userDiscount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600 font-bold uppercase tracking-tighter">Membre ({prixDetails.userDiscount}%)</span>
                            <span className="font-bold text-blue-600">-{prixDetails.userDiscountAmount?.toFixed(2)}€</span>
                          </div>
                        )}
                        <div className="bg-orange-600 rounded-2xl p-6 text-white mt-6 shadow-xl shadow-orange-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-bold uppercase tracking-widest opacity-80">{t.totalPrice || "Total"}</span>
                            <span className="text-3xl font-black">{prixDetails.prixTotal}€</span>
                          </div>
                          <div className="text-[10px] uppercase font-bold tracking-[0.2em] mt-2 opacity-70">
                            {t.for || "pour"} {prixDetails.nbNuits} {t.nights || "nuits"}
                          </div>
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleBooking}
                          disabled={reservationLoading || !prixDetails || (!currentUser && !userEmail) || !acceptCgv}
                          className="w-full mt-4 bg-emerald-600 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-emerald-700 transition shadow-2xl shadow-emerald-200 disabled:opacity-50"
                        >
                          {reservationLoading ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              {t.processing || "Action..."}
                            </div>
                          ) : (
                            (!acceptCgv ? "Accepter CGV" : (t.bookNow || "Confirmer"))
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        {hotel.images && hotel.images.length > 1 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-orange-700 mb-6">
              {t.gallery || "Gallery"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {hotel.images.map((image, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-md">
                  <img
                    src={image}
                    alt={`${hotel.nom} - ${index + 1}`}
                    className="w-full h-40 object-cover hover:scale-105 transition-transform"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="mt-12">
          <ReviewSection 
            serviceType="Hotel"
            serviceId={hotel._id}
            serviceName={hotel.nom}
          />
        </div>
      </div>
    </div>
  );
};

export default HotelDetails;
